import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db, storage } from "../Config/firebase";
import { compressImage } from "../../utils/compressImage";
import RelatorioRupturaBlocoCeramicoPrint from "./RelatorioRupturaBlocoCeramicoPrint";
import {
  calcularBlocos,
  calcularBlocosAbsorcao,
  calcularIdadeDias,
  calcularResumo,
  criarBlocos,
  criarBlocosAbsorcao,
  formatarDataBR,
  novoBloco,
  novoBlocoAbsorcao,
  numeroBR,
} from "./blocoCeramicoCalculos";
import "../CadastroExtracoes/DetalhesExtracao.css";
import { consultaTenantAtual } from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const CONFIG_BLOCO_CERAMICO = {
  colecao: "tblRupturaBlocoCeramicos",
  quantidadeCompressao: 13,
  quantidadeAbsorcao: 6,
  pastaArquivos: "bloco-ceramico",
  titulo: "Ruptura de Bloco Cerâmico",
  codigoFallback: "RE-BLC",
  tipoSoloCimento: false,
};

const CONFIG_SOLO_CIMENTO = {
  colecao: "tblEnsaiosSoloCimento",
  quantidadeCompressao: 7,
  quantidadeAbsorcao: 3,
  pastaArquivos: "solo-cimento",
  titulo: "Ensaio de Tijolo Solo-Cimento",
  codigoFallback: "RE-SC",
  tipoSoloCimento: true,
};

const larguraRecorteFotoBlocoCeramico = 930;
const alturaRecorteFotoBlocoCeramico = 520;

function carregarImagemRecorteBlocoCeramico(url) {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error("Não foi possível carregar a imagem."));
    imagem.src = url;
  });
}

async function gerarFotoBlocoCeramicoRecortada({ url, zoom, deslocamentoX, deslocamentoY }) {
  const imagem = await carregarImagemRecorteBlocoCeramico(url);
  const canvas = document.createElement("canvas");
  canvas.width = larguraRecorteFotoBlocoCeramico;
  canvas.height = alturaRecorteFotoBlocoCeramico;
  const contexto = canvas.getContext("2d");
  const escalaBase = Math.max(
    canvas.width / imagem.naturalWidth,
    canvas.height / imagem.naturalHeight
  );
  const escala = escalaBase * zoom;
  const larguraImagem = imagem.naturalWidth * escala;
  const alturaImagem = imagem.naturalHeight * escala;
  const limiteX = Math.max(0, (larguraImagem - canvas.width) / 2);
  const limiteY = Math.max(0, (alturaImagem - canvas.height) / 2);
  const x = (canvas.width - larguraImagem) / 2 + deslocamentoX * limiteX;
  const y = (canvas.height - alturaImagem) / 2 + deslocamentoY * limiteY;

  contexto.drawImage(imagem, x, y, larguraImagem, alturaImagem);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
  if (!blob) throw new Error("Não foi possível recortar a imagem.");
  return new File([blob], "bloco-ceramico-recortado.jpg", { type: "image/jpeg" });
}

const inputTabelaSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
  "& .MuiInputBase-input": { textAlign: "center", p: "7px 4px" },
};

const cellCenterSx = { textAlign: "center", verticalAlign: "middle" };
const groupedHeaderCellSx = {
  ...cellCenterSx,
  border: "1px solid #9ca3af",
  backgroundColor: "#e5e7eb",
  fontWeight: 700,
  px: "4px",
  py: "2px",
  lineHeight: 1.15,
};
const inputCellSx = { ...cellCenterSx, px: "4px", py: "3px" };

function formatarPrensaRuptura(prensa) {
  if (!prensa) return "";
  return [
    prensa.codigo,
    prensa.empresa,
    prensa.equipamento,
    prensa.serie && `Série ${prensa.serie}`,
    prensa.dataCalibracao && `Calibração ${formatarDataBR(prensa.dataCalibracao)}`,
  ].filter(Boolean).join(" - ");
}

function extrairValorResistencia(valor) {
  return String(valor || "").replace(/[^\d,.]/g, "");
}

function formatarResistenciaEsperada(valor) {
  const numero = extrairValorResistencia(valor).trim();
  return numero ? `${numero} MPa` : "";
}

function prepararEnsaioParaSalvar(ensaio) {
  return {
    ...ensaio,
    resistenciaEsperada: formatarResistenciaEsperada(ensaio?.resistenciaEsperada),
  };
}

function valorIgual(valorAtual, valorNovo) {
  return String(valorAtual ?? "") === String(valorNovo ?? "");
}

export function DetalhesSoloCimento(props) {
  return <DetalhesRupturaBlocoCeramico {...props} configuracao={CONFIG_SOLO_CIMENTO} />;
}

export default function DetalhesRupturaBlocoCeramico({ configuracao = CONFIG_BLOCO_CERAMICO }) {
  const parametros = useParams();
  const blocoCeramicoId = parametros.blocoCeramicoId || parametros.soloCimentoId;
  const COLLECTION = configuracao.colecao;
  const QUANTIDADE_BLOCOS_PADRAO = configuracao.quantidadeCompressao;
  const history = useHistory();
  const areaRecorteFotoRef = useRef(null);
  const arrasteFotoRef = useRef(null);
  const [ensaio, setEnsaio] = useState(null);
  const [blocos, setBlocos] = useState([]);
  const [blocosAbsorcao, setBlocosAbsorcao] = useState([]);
  const [calibracoesPrensa, setCalibracoesPrensa] = useState([]);
  const [celulasCarga, setCelulasCarga] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [openFotos, setOpenFotos] = useState(false);
  const [uploadingFotoIndex, setUploadingFotoIndex] = useState(null);
  const [fotosDraft, setFotosDraft] = useState([]);
  const [recorteFoto, setRecorteFoto] = useState({
    open: false,
    index: null,
    url: "",
    zoom: 1,
    deslocamentoX: 0,
    deslocamentoY: 0,
    larguraImagem: 0,
    alturaImagem: 0,
  });
  const [alterado, setAlterado] = useState(false);
  const [openCancelar, setOpenCancelar] = useState(false);
  const [navegacaoPendente, setNavegacaoPendente] = useState(null);
  const desbloquearNavegacaoRef = useRef(null);
  const ensaioOriginalRef = useRef(null);

  useEffect(() => {
    carregarEnsaio();
    carregarCalibracoesPrensa();
    carregarCelulasCarga();
  }, [blocoCeramicoId]);

  useEffect(() => {
    function confirmarSaida(event) {
      if (!alterado) return;

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", confirmarSaida);
    return () => window.removeEventListener("beforeunload", confirmarSaida);
  }, [alterado]);

  useEffect(() => {
    if (!alterado) return undefined;

    desbloquearNavegacaoRef.current = history.block((location) => {
      setNavegacaoPendente(location);
      setOpenCancelar(true);
      return false;
    });

    return () => {
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
    };
  }, [alterado, history]);

  async function carregarEnsaio() {
    const snap = await getDoc(doc(db, COLLECTION, blocoCeramicoId));
    if (!snap.exists()) {
      history.goBack();
      return;
    }
    const dados = { id: snap.id, ...snap.data() };
    const lista = calcularBlocos(
      dados.blocos?.length ? dados.blocos.slice(0, QUANTIDADE_BLOCOS_PADRAO) : criarBlocos(QUANTIDADE_BLOCOS_PADRAO),
      { dividirComprimentoArea: configuracao.tipoSoloCimento }
    );
    const listaAbsorcao = calcularBlocosAbsorcao(
      dados.blocosAbsorcao?.length ? dados.blocosAbsorcao : criarBlocosAbsorcao(configuracao.quantidadeAbsorcao)
    );
    setEnsaio(dados);
    setBlocos(lista);
    setBlocosAbsorcao(listaAbsorcao);
    ensaioOriginalRef.current = { ...dados, blocos: lista, blocosAbsorcao: listaAbsorcao };
    setAlterado(false);
  }

  async function carregarCalibracoesPrensa() {
  const q = query(collection(db, "tblCalibracaoPrensas"), orderBy("dataCalibracao", "desc"));
  const snap = await getDocs(consultaTenantAtual(q));
  setCalibracoesPrensa(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
}

async function obterUrlCertificadoEquipamento(documento) {
  if (!documento) return "";

  const urlDireta =
    documento.url ||
    documento.arquivo ||
    documento.arquivoUrl ||
    documento.urlArquivo ||
    documento.downloadURL ||
    documento.downloadUrl ||
    documento.fileUrl ||
    documento.anexoUrl ||
    documento.storageUrl ||
    documento.pathUrl ||
    documento.link ||
    documento.arquivoAtual?.url ||
    documento.file?.url ||
    "";

  if (urlDireta) return urlDireta;

  if (documento.storagePathAtual) {
    try {
      return await getDownloadURL(ref(storage, documento.storagePathAtual));
    } catch (error) {
      console.error("Erro ao buscar URL do certificado da célula de carga:", error);
      return "";
    }
  }

  return "";
}

async function carregarCelulasCarga() {
  const eqSnap = await getDocs(consultaTenantAtual(collection(db, "equipamentos")));
  const docSnap = await getDocs(consultaTenantAtual(collection(db, "eq_documentos")));

  const documentosPorEquipamento = {};

  docSnap.docs.forEach((documento) => {
    const data = documento.data();

    if (!documentosPorEquipamento[data.equipamentoId]) {
      documentosPorEquipamento[data.equipamentoId] = [];
    }

    documentosPorEquipamento[data.equipamentoId].push({
      id: documento.id,
      ...data,
    });
  });

  const listaBase = eqSnap.docs
    .map((documento) => {
      const equipamento = { id: documento.id, ...documento.data() };

      const documentos = documentosPorEquipamento[equipamento.id] || [];

      const certificadoCalibracao =
        documentos.find((docItem) => {
          const codigo = String(docItem.tipoDocumentoCodigo || "").toUpperCase();
          const nome = String(
            docItem.nomeDocumento ||
              docItem.tipoDocumentoNome ||
              docItem.tipoDocumento ||
              docItem.nome ||
              ""
          ).toUpperCase();

          return (
            codigo.includes("CERTIFICADO") ||
            nome.includes("CERTIFICADO DE CALIBRAÇÃO") ||
            nome.includes("CERTIFICADO DE CALIBRACAO")
          );
        }) || null;

      return {
        ...equipamento,
        certificadoCalibracao,
      };
    })
    .filter((item) => item.celulaCargaPrensa === true && item.ativo !== false)
    .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));

  const lista = await Promise.all(
    listaBase.map(async (equipamento) => {
      const urlCertificado = await obterUrlCertificadoEquipamento(
        equipamento.certificadoCalibracao
      );

      return {
        ...equipamento,
        certificadoCalibracao: equipamento.certificadoCalibracao
          ? {
              ...equipamento.certificadoCalibracao,
              urlCertificado,
            }
          : null,
        urlCertificadoCalibracao: urlCertificado,
      };
    })
  );

  setCelulasCarga(lista);
}

  function atualizarCampo(campo, valor) {
    setEnsaio((prev) => {
      if (valorIgual(prev?.[campo], valor)) return prev;
      setAlterado((alteradoAtual) => alteradoAtual || true);
      return { ...prev, [campo]: valor };
    });
  }

  function atualizarBloco(index, campo, valor) {
    setBlocos((prev) => {
      const itemAtual = prev[index];
      const valorCompatibilidade =
        campo === "desvioRelacaoEsquadro1"
          ? itemAtual?.desvioRelacaoEsquadro
          : campo === "planezaFaces1"
            ? itemAtual?.planezaFaces
            : undefined;
      if (
        valorIgual(itemAtual?.[campo], valor) ||
        (!itemAtual?.[campo] && valorCompatibilidade && valorIgual(valorCompatibilidade, valor))
      ) {
        return prev;
      }
      const lista = [...prev];
      lista[index] = { ...lista[index], [campo]: valor };
      setAlterado((alteradoAtual) => alteradoAtual || true);
      return calcularBlocos(lista, { dividirComprimentoArea: configuracao.tipoSoloCimento });
    });
  }

  function atualizarBlocoAbsorcao(index, campo, valor) {
    setBlocosAbsorcao((prev) => {
      if (valorIgual(prev[index]?.[campo], valor)) return prev;
      const lista = [...prev];
      lista[index] = { ...lista[index], [campo]: valor };
      setAlterado((alteradoAtual) => alteradoAtual || true);
      return calcularBlocosAbsorcao(lista);
    });
  }

  function handleEnterColuna(event, campo, index, aoConfirmar) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    if (aoConfirmar) aoConfirmar(event.currentTarget.value);

    const proximo = document.querySelector(
      `[data-bloco-ceramico-campo='${campo}'][data-bloco-ceramico-index='${index + 1}']`
    );

    if (proximo) proximo.focus();
  }

  function adicionarBloco() {
    setBlocos((prev) => calcularBlocos(
      [...prev, novoBloco(prev.length)],
      { dividirComprimentoArea: configuracao.tipoSoloCimento }
    ));
    setAlterado((prev) => prev || true);
  }

  function adicionarBlocoAbsorcao() {
    setBlocosAbsorcao((prev) => calcularBlocosAbsorcao([...prev, novoBlocoAbsorcao(prev.length)]));
    setAlterado((prev) => prev || true);
  }

  function fotosComMinimo() {
    const fotos = Array.isArray(ensaio?.relatorioFotografico) ? ensaio.relatorioFotografico : [];
    const total = Math.max(blocos.length, fotos.length);
    return Array.from({ length: total }, (_, index) => ({
      id: fotos[index]?.id || `foto-${index + 1}`,
      legenda: fotos[index]?.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
      url: fotos[index]?.url || "",
      path: fotos[index]?.path || "",
      nomeArquivo: fotos[index]?.nomeArquivo || "",
    }));
  }

  function abrirCadastroFotos() {
    setFotosDraft(fotosComMinimo().map((foto) => ({ ...foto })));
    setOpenFotos(true);
  }

  function adicionarFotoExtra() {
    setFotosDraft((prev) => [
      ...prev,
      { id: `foto-${Date.now()}`, legenda: `FOTO ${String(prev.length + 1).padStart(2, "0")}`, url: "", path: "", nomeArquivo: "" },
    ]);
  }

  function selecionarImagemFoto(index, arquivo) {
    if (!arquivo) return;
    if (!configuracao.tipoSoloCimento) {
      setRecorteFoto({
        open: true,
        index,
        url: URL.createObjectURL(arquivo),
        zoom: 1,
        deslocamentoX: 0,
        deslocamentoY: 0,
        larguraImagem: 0,
        alturaImagem: 0,
      });
      return;
    }
    const previewUrl = URL.createObjectURL(arquivo);
    setFotosDraft((prev) => prev.map((foto, itemIndex) => itemIndex === index ? { ...foto, arquivo, previewUrl, nomeArquivo: arquivo.name } : foto));
  }

  function fecharRecorteFoto() {
    if (recorteFoto.url) URL.revokeObjectURL(recorteFoto.url);
    arrasteFotoRef.current = null;
    setRecorteFoto({
      open: false,
      index: null,
      url: "",
      zoom: 1,
      deslocamentoX: 0,
      deslocamentoY: 0,
      larguraImagem: 0,
      alturaImagem: 0,
    });
  }

  async function confirmarRecorteFoto() {
    try {
      const arquivo = await gerarFotoBlocoCeramicoRecortada(recorteFoto);
      const previewUrl = URL.createObjectURL(arquivo);
      setFotosDraft((prev) => prev.map((foto, itemIndex) => {
        if (itemIndex !== recorteFoto.index) return foto;
        if (foto.previewUrl) URL.revokeObjectURL(foto.previewUrl);
        return { ...foto, arquivo, previewUrl, nomeArquivo: arquivo.name };
      }));
      fecharRecorteFoto();
    } catch (error) {
      alert(error.message);
    }
  }

  function calcularLayoutRecorteFoto() {
    const area = areaRecorteFotoRef.current;
    if (!area || !recorteFoto.larguraImagem || !recorteFoto.alturaImagem) return null;
    const larguraArea = area.clientWidth;
    const alturaArea = area.clientHeight;
    const escalaBase = Math.max(
      larguraArea / recorteFoto.larguraImagem,
      alturaArea / recorteFoto.alturaImagem
    );
    const escala = escalaBase * recorteFoto.zoom;
    const larguraImagem = recorteFoto.larguraImagem * escala;
    const alturaImagem = recorteFoto.alturaImagem * escala;
    return {
      larguraImagem,
      alturaImagem,
      limiteX: Math.max(0, (larguraImagem - larguraArea) / 2),
      limiteY: Math.max(0, (alturaImagem - alturaArea) / 2),
    };
  }

  function estiloImagemRecorteFoto() {
    const layout = calcularLayoutRecorteFoto();
    if (!layout) return { visibility: "hidden" };
    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: layout.larguraImagem,
      height: layout.alturaImagem,
      maxWidth: "none",
      transform: "translate(-50%, -50%) translate(" +
        (recorteFoto.deslocamentoX * layout.limiteX) + "px, " +
        (recorteFoto.deslocamentoY * layout.limiteY) + "px)",
      userSelect: "none",
      pointerEvents: "none",
    };
  }

  function iniciarArrasteFoto(event) {
    const layout = calcularLayoutRecorteFoto();
    if (!layout) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    arrasteFotoRef.current = {
      pointerId: event.pointerId,
      inicioX: event.clientX,
      inicioY: event.clientY,
      deslocamentoX: recorteFoto.deslocamentoX,
      deslocamentoY: recorteFoto.deslocamentoY,
      limiteX: layout.limiteX,
      limiteY: layout.limiteY,
    };
  }

  function moverFotoRecorte(event) {
    const arraste = arrasteFotoRef.current;
    if (!arraste || arraste.pointerId !== event.pointerId) return;
    const x = arraste.limiteX
      ? arraste.deslocamentoX + (event.clientX - arraste.inicioX) / arraste.limiteX
      : 0;
    const y = arraste.limiteY
      ? arraste.deslocamentoY + (event.clientY - arraste.inicioY) / arraste.limiteY
      : 0;
    setRecorteFoto((prev) => ({
      ...prev,
      deslocamentoX: Math.max(-1, Math.min(1, x)),
      deslocamentoY: Math.max(-1, Math.min(1, y)),
    }));
  }

  function finalizarArrasteFoto(event) {
    if (arrasteFotoRef.current?.pointerId === event.pointerId) {
      arrasteFotoRef.current = null;
    }
  }

  function atualizarLegendaFoto(index, legenda) {
    setFotosDraft((prev) => prev.map((foto, itemIndex) => itemIndex === index ? { ...foto, legenda } : foto));
  }

  function removerImagemFoto(index) {
    setFotosDraft((prev) => prev.map((foto, itemIndex) => itemIndex === index ? {
      ...foto,
      url: "",
      path: "",
      arquivo: null,
      previewUrl: "",
      nomeArquivo: "",
      removerPath: foto.path || foto.removerPath || "",
    } : foto));
  }

  async function salvarFotos() {
    if (!ensaio?.id) return;
    setUploadingFotoIndex(-1);
    try {
      const fotosSalvas = [];
      for (let index = 0; index < fotosDraft.length; index++) {
        const foto = fotosDraft[index];
        let fotoFinal = {
          id: foto.id || `foto-${index + 1}`,
          legenda: foto.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
          url: foto.url || "",
          path: foto.path || "",
          nomeArquivo: foto.nomeArquivo || "",
        };
        if (foto.arquivo) {
          const arquivoComprimido = await compressImage(foto.arquivo, { maxWidth: 1400, maxHeight: 1400, quality: 0.72 });
          const caminho = `${configuracao.pastaArquivos}/${ensaio.id}/relatorio-fotografico/foto-${index + 1}-${Date.now()}.jpg`;
          const storageRef = ref(storage, caminho);
          await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
          const url = await getDownloadURL(storageRef);
          if (foto.path) deleteObject(ref(storage, foto.path)).catch(() => {});
          fotoFinal = { ...fotoFinal, url, path: caminho, nomeArquivo: foto.arquivo.name };
        } else if (foto.removerPath) {
          deleteObject(ref(storage, foto.removerPath)).catch(() => {});
        }
        fotosSalvas.push(fotoFinal);
      }
      await updateDoc(doc(db, COLLECTION, ensaio.id), { relatorioFotografico: fotosSalvas, atualizadoEm: new Date() });
      setEnsaio((prev) => ({ ...prev, relatorioFotografico: fotosSalvas }));
      ensaioOriginalRef.current = { ...ensaioOriginalRef.current, relatorioFotografico: fotosSalvas };
      setFotosDraft([]);
      setOpenFotos(false);
      setMensagem("Relatório fotográfico salvo com sucesso.");
    } finally {
      setUploadingFotoIndex(null);
    }
  }

  async function salvarFotoAbsorcao(index, arquivo) {
    if (!arquivo || !ensaio?.id) return;

    const arquivoComprimido = await compressImage(arquivo, {
      maxWidth: 1400,
      maxHeight: 1400,
      quality: 0.72,
    });
    const caminho = `${configuracao.pastaArquivos}/${ensaio.id}/relatorio-fotografico/absorcao-foto-${index + 1}-${Date.now()}.jpg`;
    const storageRef = ref(storage, caminho);
    await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
    const url = await getDownloadURL(storageRef);
    const fotosExistentes = Array.isArray(ensaio.absorcaoFotos) ? ensaio.absorcaoFotos : [];
    const quantidadeFotos = configuracao.tipoSoloCimento ? 3 : 2;
    const fotosAtuais = Array.from(
      { length: Math.max(quantidadeFotos, fotosExistentes.length, index + 1) },
      (_, fotoIndex) => fotosExistentes[fotoIndex] ?? null
    );
    const fotoAnterior = fotosAtuais[index];

    if (fotoAnterior?.path) deleteObject(ref(storage, fotoAnterior.path)).catch(() => {});

    fotosAtuais[index] = {
      id: `absorcao-${index + 1}`,
      legenda: `Imagem representativa ${index + 1}`,
      url,
      path: caminho,
      nomeArquivo: arquivo.name || "",
    };

    await updateDoc(doc(db, COLLECTION, ensaio.id), {
      absorcaoFotos: fotosAtuais,
      atualizadoEm: new Date(),
    });

    setEnsaio((prev) => ({ ...prev, absorcaoFotos: fotosAtuais }));
    ensaioOriginalRef.current = { ...ensaioOriginalRef.current, absorcaoFotos: fotosAtuais };
    setMensagem("Imagem representativa da absorção salva com sucesso.");
  }

  async function salvarFotoAnaliseDimensional(arquivo) {
    if (!arquivo || !ensaio?.id) return;

    const arquivoComprimido = await compressImage(arquivo, {
      maxWidth: 1400,
      maxHeight: 1400,
      quality: 0.72,
    });
    const caminho = `${configuracao.pastaArquivos}/${ensaio.id}/relatorio-fotografico/análise-dimensional-${Date.now()}.jpg`;
    const storageRef = ref(storage, caminho);
    await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
    const url = await getDownloadURL(storageRef);
    const fotoAnterior = ensaio.fotoAnaliseDimensional;

    if (fotoAnterior?.path) deleteObject(ref(storage, fotoAnterior.path)).catch(() => {});

    const fotoAnaliseDimensional = {
      id: "análise-dimensional",
      legenda: "Imagem representativa da análise dimensional",
      url,
      path: caminho,
      nomeArquivo: arquivo.name,
    };

    await updateDoc(doc(db, COLLECTION, ensaio.id), {
      fotoAnaliseDimensional,
      atualizadoEm: new Date(),
    });

    setEnsaio((prev) => ({ ...prev, fotoAnaliseDimensional }));
    ensaioOriginalRef.current = { ...ensaioOriginalRef.current, fotoAnaliseDimensional };
    setMensagem("Imagem da análise dimensional salva com sucesso.");
  }

  async function salvarTudo() {
    setSalvando(true);
    try {
      const listaCalculada = calcularBlocos(
        blocos.slice(0, QUANTIDADE_BLOCOS_PADRAO),
        { dividirComprimentoArea: configuracao.tipoSoloCimento }
      );
      const listaAbsorcaoCalculada = calcularBlocosAbsorcao(blocosAbsorcao);
      const dadosSalvos = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, COLLECTION, blocoCeramicoId),
        original: ensaioOriginalRef.current,
        atual: { ...ensaio, blocos, blocosAbsorcao },
        preparar: (estado) => ({
          ...prepararEnsaioParaSalvar(estado),
          quantidadeAmostras: QUANTIDADE_BLOCOS_PADRAO,
          blocos: calcularBlocos(
            (estado.blocos || []).slice(0, QUANTIDADE_BLOCOS_PADRAO),
            { dividirComprimentoArea: configuracao.tipoSoloCimento }
          ),
          blocosAbsorcao: calcularBlocosAbsorcao(estado.blocosAbsorcao || []),
        }),
        metadados: { atualizadoEm: new Date() },
      });
      const salvoTela = { ...dadosSalvos, id: blocoCeramicoId };
      setBlocos(salvoTela.blocos || listaCalculada);
      setBlocosAbsorcao(salvoTela.blocosAbsorcao || listaAbsorcaoCalculada);
      setEnsaio(salvoTela);
      ensaioOriginalRef.current = salvoTela;
      setAlterado(false);
      setMensagem("Ensaio salvo com sucesso.");
    } finally {
      setSalvando(false);
    }
  }

  async function confirmarCancelamentoAlteracoes() {
    setOpenCancelar(false);

    await carregarEnsaio();
    setMensagem("Alterações canceladas.");

    if (navegacaoPendente) {
      const destino = `${navegacaoPendente.pathname}${navegacaoPendente.search || ""}${navegacaoPendente.hash || ""}`;
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
      setNavegacaoPendente(null);
      history.push(destino);
    }
  }

  const resumo = useMemo(
    () => calcularResumo(blocos, blocosAbsorcao, { dividirComprimentoArea: configuracao.tipoSoloCimento }),
    [blocos, blocosAbsorcao, configuracao.tipoSoloCimento]
  );
  const idade = ensaio?.dataFabricacaoNaoInformada
    ? "-"
    : calcularIdadeDias(ensaio?.dataFabricacao, ensaio?.dataEnsaio);

  if (!ensaio) return null;

  const bloqueado = salvando;

  const camposTabela = [
    ["bloco", "Nº"],
    ["massa", "Massa"],
    ["largura1", "L1"],
    ["largura2", "L2"],
    ["comprimento1", "C1"],
    ["comprimento2", "C2"],
    ["altura1", "H1"],
    ["altura2", "H2"],
    ["cargaRupturaTf", "Ruptura Tf"],
  ];

  const camposDimensoesEntrada = camposTabela.filter(([campo]) =>
    ["largura1", "largura2", "comprimento1", "comprimento2", "altura1", "altura2"].includes(campo)
  );

  const camposParedes = [
    ["septo1", "Septo 1"],
    ["septo2", "Septo 2"],
    ["septo3", "Septo 3"],
    ["septo4", "Septo 4"],
    ["parede1", "Parede 1"],
    ["parede2", "Parede 2"],
    ["parede3", "Parede 3"],
    ["parede4", "Parede 4"],
    ["desvioRelacaoEsquadro", "Desvio esquadro"],
    ["planezaFaces", "Planeza faces"],
  ];

  const relatorioEnsaio = { ...ensaio, blocos, blocosAbsorcao };

  return (
    <>
      {modoPrint && (
        <RelatorioRupturaBlocoCeramicoPrint
          ensaio={relatorioEnsaio}
          tipoSoloCimento={configuracao.tipoSoloCimento}
          fotosRelatorio={(ensaio.relatorioFotografico || []).filter((foto) => foto.url)}
          onVoltar={() => setModoPrint(false)}
        />
      )}
      <Box sx={{ display: modoPrint ? "none" : "block", minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
      <Navbar />
      <Box className="extracao-actions">
        <Box className="extracao-actions-titulos">
          <Typography component="h1">{configuracao.titulo}</Typography>
          <Typography component="p">{ensaio.codigo || configuracao.codigoFallback} Rev.00 - {formatarDataBR(ensaio.dataRelatorio || ensaio.dataEnsaio)}</Typography>
        </Box>
        <Box className="extracao-actions-botoes">
          {alterado && (
            <>
          <Button variant="contained" onClick={salvarTudo} disabled={bloqueado} startIcon={<SaveIcon />}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
          <Button variant="outlined" color="error" onClick={() => {
            setNavegacaoPendente(null);
            setOpenCancelar(true);
          }} disabled={bloqueado}>
            Cancelar alterações
          </Button>
            </>
          )}
          <Tooltip title="Visualizar relatório">
            <span>
              <IconButton color="primary" onClick={() => setModoPrint(true)} disabled={bloqueado || alterado}>
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Relatório fotográfico">
            <span>
              <IconButton color="primary" onClick={abrirCadastroFotos} disabled={bloqueado || alterado}>
                <PhotoLibraryIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Imprimir relatório">
            <span>
              <IconButton color="primary" onClick={() => setModoPrint(true)} disabled={bloqueado || alterado}>
                <PrintIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="outlined" onClick={() => history.goBack()} disabled={bloqueado || alterado} startIcon={<ArrowBackIcon />}>
            Voltar
          </Button>
        </Box>
      </Box>

      <Box className="extracao-page" component="form" autoComplete="off" sx={{ backgroundColor: "#f4f6f8" }}>
        {mensagem && <Typography className="extracao-mensagem">{mensagem}</Typography>}

        <Dialog open={openCancelar} onClose={() => {
          setNavegacaoPendente(null);
          setOpenCancelar(false);
        }} fullWidth maxWidth="xs">
          <DialogTitle>Cancelar alterações?</DialogTitle>
          <DialogContent dividers>
            <Typography>As alterações feitas nesta tela serão descartadas.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setNavegacaoPendente(null);
              setOpenCancelar(false);
            }}>Continuar editando</Button>
            <Button color="error" variant="contained" onClick={confirmarCancelamentoAlteracoes}>Descartar alterações</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openFotos} onClose={(event, reason) => {
          if (uploadingFotoIndex !== null || reason === "backdropClick") return;
          setOpenFotos(false);
        }} fullWidth maxWidth="md">
          <DialogTitle>Relatório fotográfico</DialogTitle>
          <DialogContent dividers>
            <Box className="fotos-extracao-grid">
              {fotosDraft.map((foto, index) => (
                <Paper className="foto-extracao-card" key={foto.id || index} variant="outlined">
                  <Box className="foto-extracao-preview">
                    {foto.previewUrl || foto.url ? <img src={foto.previewUrl || foto.url} alt={foto.legenda || `Foto ${index + 1}`} /> : <AddPhotoAlternateIcon />}
                  </Box>
                  <TextField size="small" label="Legenda" value={foto.legenda} onChange={(e) => atualizarLegendaFoto(index, e.target.value)} fullWidth />
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button component="label" size="small" variant="contained" disabled={uploadingFotoIndex !== null}>
                      {foto.arquivo ? "Selecionada" : "Imagem"}
                      <input hidden accept="image/*" type="file" onChange={(e) => {
                        selecionarImagemFoto(index, e.target.files?.[0]);
                        e.target.value = "";
                      }} />
                    </Button>
                    <IconButton size="small" color="error" onClick={() => removerImagemFoto(index)} disabled={(!foto.url && !foto.previewUrl) || uploadingFotoIndex !== null}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={adicionarFotoExtra} startIcon={<AddPhotoAlternateIcon />} disabled={uploadingFotoIndex !== null}>Acrescentar imagem</Button>
            <Button onClick={() => setOpenFotos(false)} disabled={uploadingFotoIndex !== null}>Cancelar</Button>
            <Button onClick={salvarFotos} variant="contained" disabled={uploadingFotoIndex !== null}>{uploadingFotoIndex !== null ? "Salvando..." : "Salvar"}</Button>
          </DialogActions>
        </Dialog>

        {!configuracao.tipoSoloCimento && (
          <Dialog open={recorteFoto.open} onClose={fecharRecorteFoto} fullWidth maxWidth="md">
            <DialogTitle>Enquadrar foto do relatório</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Arraste a foto para escolher o enquadramento e ajuste o tamanho pelo zoom.
              </Typography>
              <Box
                ref={areaRecorteFotoRef}
                onPointerDown={iniciarArrasteFoto}
                onPointerMove={moverFotoRecorte}
                onPointerUp={finalizarArrasteFoto}
                onPointerCancel={finalizarArrasteFoto}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: `${larguraRecorteFotoBlocoCeramico} / ${alturaRecorteFotoBlocoCeramico}`,
                  overflow: "hidden",
                  backgroundColor: "#111",
                  border: "2px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                  cursor: "grab",
                  touchAction: "none",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                {recorteFoto.url && (
                  <Box
                    component="img"
                    src={recorteFoto.url}
                    alt="Pré-visualização do enquadramento"
                    onLoad={(event) => {
                      const larguraImagem = event.currentTarget.naturalWidth;
                      const alturaImagem = event.currentTarget.naturalHeight;
                      setRecorteFoto((prev) => ({ ...prev, larguraImagem, alturaImagem }));
                    }}
                    sx={estiloImagemRecorteFoto()}
                  />
                )}
              </Box>
              <Typography fontWeight={700} mt={3}>Zoom</Typography>
              <Slider
                value={recorteFoto.zoom}
                min={1}
                max={3}
                step={0.05}
                onChange={(event, valor) => setRecorteFoto((prev) => ({ ...prev, zoom: valor }))}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={fecharRecorteFoto}>Cancelar</Button>
              <Button variant="contained" onClick={confirmarRecorteFoto}>Aplicar enquadramento</Button>
            </DialogActions>
          </Dialog>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>DADOS DO ENSAIO</Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={2}>
            <TextField label="Data relatório" type="date" InputLabelProps={{ shrink: true }} value={ensaio.dataRelatorio || ""} size="small" onChange={(e) => atualizarCampo("dataRelatorio", e.target.value)} />
            {configuracao.tipoSoloCimento && <TextField label="Data recebimento" type="date" InputLabelProps={{ shrink: true }} value={ensaio.dataRecebimento || ""} size="small" onChange={(e) => atualizarCampo("dataRecebimento", e.target.value)} />}
            <TextField label="Data ensaio" type="date" InputLabelProps={{ shrink: true }} value={ensaio.dataEnsaio || ""} size="small" onChange={(e) => atualizarCampo("dataEnsaio", e.target.value)} />
            <TextField
              label="Data fabricação"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={ensaio.dataFabricacao || ""}
              size="small"
              onChange={(e) => atualizarCampo("dataFabricacao", e.target.value)}
              disabled={Boolean(ensaio.dataFabricacaoNaoInformada)}
            />
            <TextField label="Idade (dias)" value={idade || ""} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Cliente" value={ensaio.cliente || "-"} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Obra" value={ensaio.obra || "-"} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Código" value={ensaio.codigo || "-"} size="small" onChange={(e) => atualizarCampo("codigo", e.target.value)} />
            <TextField label={configuracao.tipoSoloCimento ? "Tijolo" : "Bloco"} value={ensaio.blocoDescricao || ""} size="small" onChange={(e) => atualizarCampo("blocoDescricao", e.target.value)} />
            {configuracao.tipoSoloCimento && <TextField label="Procedência" value={ensaio.procedencia || ""} size="small" onChange={(e) => atualizarCampo("procedencia", e.target.value)} />}
            {configuracao.tipoSoloCimento && <TextField label="Lote" value={ensaio.lote || ""} size="small" onChange={(e) => atualizarCampo("lote", e.target.value)} />}
            {configuracao.tipoSoloCimento && <TextField label="Endereço" value={ensaio.endereco || ""} size="small" onChange={(e) => atualizarCampo("endereco", e.target.value)} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />}
            <TextField label="Local de aplicação" value={ensaio.localAplicacao || ""} size="small" onChange={(e) => atualizarCampo("localAplicacao", e.target.value)} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
            <TextField label="Dimensões declaradas" value={ensaio.dimensoesDeclaradas || ""} size="small" onChange={(e) => atualizarCampo("dimensoesDeclaradas", e.target.value)} />
            <TextField label="Resistência esperada" value={formatarResistenciaEsperada(ensaio.resistenciaEsperada)} size="small" onChange={(e) => atualizarCampo("resistenciaEsperada", formatarResistenciaEsperada(e.target.value))} />
            <TextField
  select
  label="Prensa utilizada"
  value={ensaio.prensaRuptura?.id || ""}
  size="small"
  onChange={(e) => {
    const prensa = calibracoesPrensa.find((item) => item.id === e.target.value);

    atualizarCampo("prensaRuptura", prensa ? { id: prensa.id, ...prensa } : null);

    if (prensa) {
      atualizarCampo("celulaCargaRuptura", null);
    }
  }}
>
  <MenuItem value="">Selecione a prensa/certificado</MenuItem>
  {calibracoesPrensa.map((prensa) => (
    <MenuItem key={prensa.id} value={prensa.id}>
      {formatarPrensaRuptura(prensa)}
    </MenuItem>
  ))}
</TextField>

<TextField
  select
  label="Célula de carga"
  value={ensaio.celulaCargaRuptura?.id || ""}
  size="small"
  onChange={(e) => {
  const celula = celulasCarga.find((item) => item.id === e.target.value);

  atualizarCampo(
    "celulaCargaRuptura",
    celula
      ? {
          id: celula.id,
          nome: celula.nome || "",
          descricao: celula.descricao || "",
          certificadoCalibracao: celula.certificadoCalibracao || null,
          certificado: celula.certificado || null,
          urlCertificadoCalibracao:
            celula.urlCertificadoCalibracao ||
            celula.certificadoCalibracao?.urlCertificado ||
            ""
        }
      : null
  );

  if (celula) {
    atualizarCampo("prensaRuptura", null);
  }
}}
>
  <MenuItem value="">Selecione a célula de carga</MenuItem>
  {celulasCarga.map((celula) => (
    <MenuItem key={celula.id} value={celula.id}>
      {[celula.nome, celula.descricao].filter(Boolean).join(" - ")}
    </MenuItem>
  ))}
</TextField>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} mb={2}>
            <Typography fontWeight={700}>COMPRESSÃO, DIMENSÕES E ABSORÇÃO</Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={adicionarBloco} disabled={bloqueado}>Adicionar {configuracao.tipoSoloCimento ? "tijolo" : "bloco"}</Button>
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 1260,
                ...(configuracao.tipoSoloCimento && {
                  "& tbody td": { border: "1px solid #9ca3af" },
                  "& tbody tr:nth-of-type(even)": { backgroundColor: "#dbe8f5" },
                }),
              }}
            >
              <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell rowSpan={3} sx={{ ...groupedHeaderCellSx, width: 72 }}>BLOCO<br />Nº</TableCell>
                  <TableCell rowSpan={3} sx={{ ...groupedHeaderCellSx, width: 92 }}>Massa<br />(g)</TableCell>
                  <TableCell colSpan={9} sx={groupedHeaderCellSx}>DIMENSÕES (mm)</TableCell>
                  <TableCell colSpan={3} sx={groupedHeaderCellSx}>RUPTURA</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2} sx={groupedHeaderCellSx}>Largura (L)</TableCell>
                  <TableCell colSpan={2} sx={groupedHeaderCellSx}>Comprim. (C)</TableCell>
                  <TableCell colSpan={2} sx={groupedHeaderCellSx}>Altura (H)</TableCell>
                  <TableCell colSpan={3} sx={groupedHeaderCellSx}>Média</TableCell>
                  <TableCell rowSpan={2} sx={{ ...groupedHeaderCellSx, width: 96 }}>Carga<br />(Tf)</TableCell>
                  <TableCell rowSpan={2} sx={{ ...groupedHeaderCellSx, width: 92 }}>Área<br />(mm²)</TableCell>
                  <TableCell rowSpan={2} sx={{ ...groupedHeaderCellSx, width: 92 }}>Tensão<br />(MPa)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={groupedHeaderCellSx}>1ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>1ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>1ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª Det.</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>L</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>C</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>H</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blocos.map((item, index) => (
                  <TableRow key={index}>
                    {["bloco", "massa"].map((campo) => {
                      const label = campo === "bloco" ? "Nº" : "Massa";

                      return (
                        <TableCell key={campo} sx={{ ...inputCellSx, minWidth: campo === "bloco" ? 72 : 92 }}>
                          <TextField
                            key={`compressao-${index}-${campo}-${item[campo] || ""}`}
                            size="small"
                            data-label={label}
                            defaultValue={item[campo] || ""}
                            onBlur={(e) => {
                              atualizarBloco(index, campo, e.target.value);
                            }}
                            onKeyDown={(e) => handleEnterColuna(e, campo, index, (valor) => atualizarBloco(index, campo, valor))}
                            sx={inputTabelaSx}
                            inputProps={{
                              "data-bloco-ceramico-campo": campo,
                              "data-bloco-ceramico-index": index,
                            }}
                          />
                        </TableCell>
                      );
                    })}
                    {camposDimensoesEntrada.map(([campo, label]) => (
                      <TableCell key={campo} sx={{ ...inputCellSx, minWidth: 90 }}>
                        <TextField
                          key={`compressao-${index}-${campo}-${item[campo] || ""}`}
                          size="small"
                          data-label={label}
                          defaultValue={item[campo] || ""}
                          onBlur={(e) => {
                            atualizarBloco(index, campo, e.target.value);
                          }}
                          onKeyDown={(e) => handleEnterColuna(e, campo, index, (valor) => atualizarBloco(index, campo, valor))}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-bloco-ceramico-campo": campo,
                            "data-bloco-ceramico-index": index,
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={cellCenterSx}>{numeroBR(item.larguraMedia, 1)}</TableCell>
                    <TableCell sx={cellCenterSx}>{numeroBR(item.comprimentoMedia, 1)}</TableCell>
                    <TableCell sx={cellCenterSx}>{numeroBR(item.alturaMedia, 1)}</TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 96 }}>
                      <TextField
                        key={`compressao-${index}-cargaRupturaTf-${item.cargaRupturaTf || ""}`}
                        size="small"
                        data-label="Carga Tf"
                        defaultValue={item.cargaRupturaTf || ""}
                        onBlur={(e) => {
                          atualizarBloco(index, "cargaRupturaTf", e.target.value);
                        }}
                        onKeyDown={(e) => handleEnterColuna(e, "cargaRupturaTf", index, (valor) => atualizarBloco(index, "cargaRupturaTf", valor))}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-bloco-ceramico-campo": "cargaRupturaTf",
                          "data-bloco-ceramico-index": index,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellCenterSx}>{numeroBR(item.area, 0)}</TableCell>
                    <TableCell sx={cellCenterSx}><Typography fontWeight={700}>{numeroBR(item.tensaoRupturaMpa, 2)}</Typography></TableCell>
                  </TableRow>
                ))}
                {configuracao.tipoSoloCimento && (
                  <>
                    <TableRow>
                      <TableCell colSpan={10} />
                      <TableCell colSpan={2} sx={cellCenterSx}>Valor mínimo</TableCell>
                      <TableCell sx={cellCenterSx}>(MPa)</TableCell>
                      <TableCell sx={{ ...cellCenterSx, color: "#173f79", fontWeight: 700 }}>
                        {numeroBR(resumo.resistenciaMinima, 1)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} />
                      <TableCell colSpan={2} sx={cellCenterSx}>Resistência média</TableCell>
                      <TableCell sx={cellCenterSx}>(MPa)</TableCell>
                      <TableCell sx={{ ...cellCenterSx, color: "#173f79", fontWeight: 700 }}>
                        {numeroBR(resumo.resistenciaMedia, 1)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </Box>
          {!configuracao.tipoSoloCimento && <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "290px 1fr" }, gap: 0, alignItems: "stretch" }}>
            <Box sx={{ border: "1px solid #9ca3af", borderRight: { md: 0 }, p: 0.5, display: "grid", gap: 1 }}>
              <Box sx={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", bgcolor: "#f8fafc" }}>
                {ensaio.fotoAnaliseDimensional?.url ? (
                  <img src={ensaio.fotoAnaliseDimensional.url} alt="Análise dimensional" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <AddPhotoAlternateIcon color="disabled" />
                )}
              </Box>
              <Button component="label" variant="contained" size="small" disabled={bloqueado}>
                {ensaio.fotoAnaliseDimensional?.url ? "Substituir imagem" : "Anexar imagem"}
                <input hidden accept="image/*" type="file" onChange={(e) => salvarFotoAnaliseDimensional(e.target.files?.[0])} />
              </Button>
            </Box>
            <Table size="small" sx={{ tableLayout: "fixed", border: "1px solid #9ca3af", "& td": { border: "1px solid #9ca3af", textAlign: "center", py: 1 } }}>
              <TableBody>
                <TableRow>
                  <TableCell>Tolerância individual admissível para as dimensões</TableCell>
                  <TableCell sx={{ width: 80 }}>(mm)</TableCell>
                  <TableCell sx={{ width: 80, color: "#173f79", fontWeight: 700 }}>5,0</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Tolerância adm. em relação à média das dimensões</TableCell>
                  <TableCell>(mm)</TableCell>
                  <TableCell sx={{ color: "#173f79", fontWeight: 700 }}>3,0</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Valor mínimo</TableCell>
                  <TableCell>(MPa)</TableCell>
                  <TableCell sx={{ color: "#173f79", fontWeight: 700 }}>
                    {numeroBR(resumo.resistenciaMinima, 2)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Resistência média</TableCell>
                  <TableCell>(MPa)</TableCell>
                  <TableCell sx={{ color: "#173f79", fontWeight: 700 }}>{numeroBR(resumo.resistenciaMedia, 1)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>}
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} mb={2}>
            <Typography fontWeight={700}>ABSORÇÃO DE ÁGUA</Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={adicionarBlocoAbsorcao} disabled={bloqueado}>Adicionar {configuracao.tipoSoloCimento ? "tijolo" : "bloco"}</Button>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "430px 1fr" }, gap: 2, alignItems: "start", mb: 2 }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "fixed", width: 374 }}>
              <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ ...cellCenterSx, width: 58 }}>Bloco Nº</TableCell>
                  <TableCell sx={{ ...cellCenterSx, width: 102 }}>Seco na estufa</TableCell>
                  <TableCell sx={{ ...cellCenterSx, width: 112 }}>Massa úmida (Mu)</TableCell>
                  <TableCell sx={{ ...cellCenterSx, width: 92 }}>Absorção (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blocosAbsorcao.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ ...inputCellSx, width: 58 }}>
                      <TextField
                        key={`absorcao-${index}-bloco-${item.bloco || ""}`}
                        size="small"
                        data-label="Bloco Nº"
                        defaultValue={item.bloco || ""}
                        onBlur={(e) => {
                          atualizarBlocoAbsorcao(index, "bloco", e.target.value);
                        }}
                        onKeyDown={(e) => handleEnterColuna(e, "absorcao-bloco", index, (valor) => atualizarBlocoAbsorcao(index, "bloco", valor))}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-bloco-ceramico-campo": "absorcao-bloco",
                          "data-bloco-ceramico-index": index,
                        }}
                      />
                    </TableCell>
                    {[
                      ["massaSeca", "Seco"],
                      ["massaUmida", "Úmida"],
                    ].map(([campo, label]) => (
                      <TableCell key={campo} sx={{ ...inputCellSx, width: campo === "massaSeca" ? 102 : 112 }}>
                        <TextField
                          key={`absorcao-${index}-${campo}-${item[campo] || ""}`}
                          size="small"
                          data-label={label}
                          defaultValue={item[campo] || ""}
                          onBlur={(e) => {
                            atualizarBlocoAbsorcao(index, campo, e.target.value);
                          }}
                          onKeyDown={(e) => handleEnterColuna(e, `absorcao-${campo}`, index, (valor) => atualizarBlocoAbsorcao(index, campo, valor))}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-bloco-ceramico-campo": `absorcao-${campo}`,
                            "data-bloco-ceramico-index": index,
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...cellCenterSx, width: 92 }}>
                      <Typography fontWeight={700}>{numeroBR(item.absorcao, 1)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1}>Imagens representativas do ensaio de absorção</Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                {Array.from({ length: configuracao.tipoSoloCimento ? 3 : 2 }, (_, index) => index).map((index) => {
                  const foto = ensaio.absorcaoFotos?.[index];

                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5, display: "grid", gap: 1 }}>
                      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", bgcolor: "#f8fafc" }}>
                        {foto?.url ? (
                          <img src={foto.url} alt={foto.legenda || `Imagem ${index + 1}`} style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
                        ) : (
                          <AddPhotoAlternateIcon color="disabled" />
                        )}
                      </Box>
                      <Button component="label" variant="contained" disabled={bloqueado}>
                        {foto?.url ? "Substituir imagem" : "Anexar imagem"}
                        <input hidden accept="image/*" type="file" onChange={(e) => salvarFotoAbsorcao(index, e.target.files?.[0])} />
                      </Button>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>

        {!configuracao.tipoSoloCimento && <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>PAREDES, SEPTOS E RESUMO</Typography>
          <Box sx={{ overflowX: "auto", mb: 2 }}>
            <Table size="small" sx={{ minWidth: 1220 }}>
              <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell rowSpan={4} sx={{ ...groupedHeaderCellSx, width: 72 }}>BLOCO<br /><br />Nº</TableCell>
                  <TableCell colSpan={9} sx={groupedHeaderCellSx}>DIMENSÕES</TableCell>
                  <TableCell colSpan={2} rowSpan={3} sx={{ ...groupedHeaderCellSx, width: 180 }}>Desvio em<br />Relação ao<br />Esquadro (mm)</TableCell>
                  <TableCell colSpan={2} rowSpan={3} sx={{ ...groupedHeaderCellSx, width: 180 }}>Det. Planeza<br />das Faces<br />(mm)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={groupedHeaderCellSx}>SÉPTOS</TableCell>
                  <TableCell colSpan={5} sx={groupedHeaderCellSx}>PAREDES EXTERNAS</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={groupedHeaderCellSx}>Determinações</TableCell>
                  <TableCell colSpan={5} sx={groupedHeaderCellSx}>Determinações</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={groupedHeaderCellSx}>1ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>3ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>4ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>1ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>3ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>4ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>soma<br />paredes</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>1ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>1ª</TableCell>
                  <TableCell sx={groupedHeaderCellSx}>2ª</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blocos.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={cellCenterSx}>{item.bloco}</TableCell>
                    {[
                      ["septo1", "Septo 1"],
                      ["septo2", "Septo 2"],
                      ["septo3", "Septo 3"],
                      ["septo4", "Septo 4"],
                      ["parede1", "Parede 1"],
                      ["parede2", "Parede 2"],
                      ["parede3", "Parede 3"],
                      ["parede4", "Parede 4"],
                    ].map(([campo, label]) => (
                      <TableCell key={campo} sx={{ ...inputCellSx, minWidth: 92 }}>
                        <TextField
                          key={`paredes-${index}-${campo}-${item[campo] || ""}`}
                          size="small"
                          data-label={label}
                          defaultValue={item[campo] || ""}
                          onBlur={(e) => {
                            atualizarBloco(index, campo, e.target.value);
                          }}
                          onKeyDown={(e) => handleEnterColuna(e, campo, index, (valor) => atualizarBloco(index, campo, valor))}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-bloco-ceramico-campo": campo,
                            "data-bloco-ceramico-index": index,
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={cellCenterSx}>{numeroBR(item.somaParedes, 1)}</TableCell>
                    {[
                      ["desvioRelacaoEsquadro1", "Desvio esquadro 1"],
                      ["desvioRelacaoEsquadro2", "Desvio esquadro 2"],
                      ["planezaFaces1", "Planeza 1"],
                      ["planezaFaces2", "Planeza 2"],
                    ].map(([campo, label]) => (
                      <TableCell key={campo} sx={{ ...inputCellSx, minWidth: 90 }}>
                        <TextField
                          key={`paredes-${index}-${campo}-${item[campo] || ""}-${campo === "desvioRelacaoEsquadro1" ? item.desvioRelacaoEsquadro || "" : ""}-${campo === "planezaFaces1" ? item.planezaFaces || "" : ""}`}
                          size="small"
                          data-label={label}
                          defaultValue={
                            campo === "desvioRelacaoEsquadro1"
                              ? item[campo] || item.desvioRelacaoEsquadro || ""
                              : campo === "planezaFaces1"
                                ? item[campo] || item.planezaFaces || ""
                                : item[campo] || ""
                          }
                          onBlur={(e) => {
                            atualizarBloco(index, campo, e.target.value);
                          }}
                          onKeyDown={(e) => handleEnterColuna(e, campo, index, (valor) => atualizarBloco(index, campo, valor))}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-bloco-ceramico-campo": campo,
                            "data-bloco-ceramico-index": index,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={2}>
            <TextField label="Resistência média (MPa)" value={numeroBR(resumo.resistenciaMedia, 1)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Absorção média (%)" value={numeroBR(resumo.absorcaoMedia, 1)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Observação" value={ensaio.observacao || ""} onChange={(e) => atualizarCampo("observacao", e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
          </Box>
        </Paper>}
        {configuracao.tipoSoloCimento && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={2}>
              <TextField label="Resistência mínima (MPa)" value={numeroBR(resumo.resistenciaMinima, 2)} size="small" InputProps={{ readOnly: true }} />
              <TextField label="Resistência média (MPa)" value={numeroBR(resumo.resistenciaMedia, 1)} size="small" InputProps={{ readOnly: true }} />
              <TextField label="Absorção média (%)" value={numeroBR(resumo.absorcaoMedia, 1)} size="small" InputProps={{ readOnly: true }} />
              <TextField label="Observação" value={ensaio.observacao || ""} onChange={(e) => atualizarCampo("observacao", e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: "auto", md: "span 3" } }} />
            </Box>
          </Paper>
        )}
      </Box>
      </Box>
    </>
  );
}
